// Medical Scores Database - Complete Collection Part 4
// Scores N-Z from medicalcul.free.fr

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Users,
  Zap,
  Target,
  Shield
} from 'lucide-react';

export const SCORES_PART_4 = {
  // Neer - Fracture humérus
  neer: {
    name: 'Classification de Neer',
    category: 'Orthopédie',
    icon: Bone,
    description: 'Fractures extrémité supérieure humérus',
    color: 'stone',
    fields: [
      { id: 'parts', label: 'Nombre de parties déplacées (>1cm ou >45°)', type: 'select', options: [
        { value: 1, label: '1 partie - Non déplacée' },
        { value: 2, label: '2 parties' },
        { value: 3, label: '3 parties' },
        { value: 4, label: '4 parties' }
      ]},
      { id: 'dislocation', label: 'Luxation associée', type: 'checkbox', points: 0 },
      { id: 'impaction', label: 'Fracture impaction/enfoncement', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      return { score: values.parts || 1, max: 4 };
    },
    interpret: (score, calculated, values) => {
      const lux = values.dislocation ? ' + luxation' : '';
      const imp = values.impaction ? ' (impactée)' : '';
      if (score === 1) return { level: 'success', text: `Neer ${score} partie${lux}${imp}`, detail: 'Traitement conservateur habituel' };
      if (score === 2) return { level: 'warning', text: `Neer ${score} parties${lux}${imp}`, detail: 'Traitement fonctionnel ou chirurgical selon déplacement' };
      if (score === 3) return { level: 'danger', text: `Neer ${score} parties${lux}${imp}`, detail: 'Chirurgie le plus souvent (ostéosynthèse ou prothèse)' };
      return { level: 'danger', text: `Neer ${score} parties${lux}${imp}`, detail: 'Chirurgie nécessaire - Prothèse souvent indiquée' };
    },
    formula: 'Neer: 1-4 parties selon déplacement (>1cm ou >45°)'
  },

  // ORACLE - MTEV
  oracle: {
    name: 'Score ORACLE',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Durée anticoagulation après TVP proximale idiopathique',
    color: 'rose',
    fields: [
      { id: 'dimer', label: 'D-dimères élevés 1 mois après arrêt AVK', type: 'checkbox', points: 2 },
      { id: 'age', label: 'Âge ≤ 50 ans', type: 'checkbox', points: 1 },
      { id: 'male', label: 'Sexe masculin', type: 'checkbox', points: 1 },
      { id: 'aptt', label: 'TCA prolongé de base', type: 'checkbox', points: -2 },
      { id: 'bmi', label: 'IMC ≥ 30', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.dimer) total += 2;
      if (values.age) total += 1;
      if (values.male) total += 1;
      if (values.aptt) total -= 2;
      if (values.bmi) total += 1;
      return { score: total, max: 5, min: -2 };
    },
    interpret: (score) => {
      if (score <= 0) return { level: 'success', text: `ORACLE ${score}`, detail: 'Faible risque récidive - Arrêt anticoagulation possible à 3-6 mois' };
      if (score <= 2) return { level: 'warning', text: `ORACLE ${score}`, detail: 'Risque intermédiaire - Décision individualisée' };
      return { level: 'danger', text: `ORACLE ${score}`, detail: 'Risque élevé de récidive - Anticoagulation prolongée' };
    },
    formula: 'ORACLE = D-dimères + Âge + Sexe + TCA + IMC'
  },

  // Paquets-années (déjà présent mais version améliorée)
  packYears: {
    name: 'Paquets-années',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Quantification tabagisme cumulé',
    color: 'sky',
    fields: [
      { id: 'cigarettesPerDay', label: 'Cigarettes par jour (moyenne)', type: 'number', min: 1, max: 100 },
      { id: 'yearsSmoked', label: 'Durée tabagisme (années)', type: 'number', min: 1, max: 80 }
    ],
    calculate: (values) => {
      const cig = values.cigarettesPerDay || 20;
      const years = values.yearsSmoked || 1;
      const pa = (cig / 20) * years;
      return { score: pa.toFixed(1), max: '', unit: 'PA' };
    },
    interpret: (score) => {
      const pa = parseFloat(score);
      if (pa < 10) return { level: 'success', text: `${score} PA`, detail: 'Tabagisme modéré' };
      if (pa < 20) return { level: 'warning', text: `${score} PA`, detail: 'Tabagisme significatif - Dépistage BPCO recommandé' };
      if (pa < 30) return { level: 'danger', text: `${score} PA`, detail: 'Tabagisme important - Dépistage BPCO et cancer bronchique' };
      return { level: 'danger', text: `${score} PA`, detail: 'Tabagisme très important - Scanner thoracique faible dose à discuter' };
    },
    formula: 'PA = (cigarettes/jour ÷ 20) × années'
  },

  // PAPPA - Appendicite pédiatrique
  pas: {
    name: 'Pediatric Appendicitis Score',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Probabilité appendicite chez l\'enfant',
    color: 'violet',
    fields: [
      { id: 'migration', label: 'Migration douleur vers FID', type: 'checkbox', points: 1 },
      { id: 'anorexia', label: 'Anorexie', type: 'checkbox', points: 1 },
      { id: 'nausea', label: 'Nausées/Vomissements', type: 'checkbox', points: 1 },
      { id: 'rif', label: 'Douleur FID', type: 'checkbox', points: 2 },
      { id: 'coughPain', label: 'Douleur toux/percussion/saut', type: 'checkbox', points: 2 },
      { id: 'rebound', label: 'Défense FID', type: 'checkbox', points: 1 },
      { id: 'fever', label: 'Fièvre > 38°C', type: 'checkbox', points: 1 },
      { id: 'leukocytosis', label: 'Leucocytes > 10 G/L', type: 'checkbox', points: 1 },
      { id: 'neutrophilia', label: 'PNN > 75%', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.migration) total += 1;
      if (values.anorexia) total += 1;
      if (values.nausea) total += 1;
      if (values.rif) total += 2;
      if (values.coughPain) total += 2;
      if (values.rebound) total += 1;
      if (values.fever) total += 1;
      if (values.leukocytosis) total += 1;
      if (values.neutrophilia) total += 1;
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `PAS ${score}/10`, detail: 'Appendicite peu probable - Surveillance, pas d\'imagerie' };
      if (score <= 6) return { level: 'warning', text: `PAS ${score}/10`, detail: 'Appendicite possible - Échographie recommandée' };
      return { level: 'danger', text: `PAS ${score}/10`, detail: 'Appendicite très probable - Avis chirurgical' };
    },
    formula: 'PAS = Migration + Anorexie + Nausées + Douleur FID(2) + Toux/Percussion(2) + Défense + Fièvre + Leuco + PNN'
  },

  // PECARN - Trauma crânien pédiatrique
  pecarn: {
    name: 'Règle PECARN',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Traumatisme crânien léger chez l\'enfant',
    color: 'violet',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 'young', label: '< 2 ans' },
        { value: 'old', label: '≥ 2 ans' }
      ]},
      { id: 'gcs', label: 'GCS < 15', type: 'checkbox', points: 1 },
      { id: 'mentalStatus', label: 'État mental altéré (agitation, somnolence, répétition)', type: 'checkbox', points: 1 },
      { id: 'skull', label: 'Fracture crâne palpable (< 2 ans) ou Suspicion fracture base (> 2 ans)', type: 'checkbox', points: 1 },
      { id: 'hematoma', label: 'Hématome scalp (non frontal si < 2 ans)', type: 'checkbox', points: 1 },
      { id: 'locFew', label: 'Perte de conscience > 5 sec', type: 'checkbox', points: 1 },
      { id: 'mechanism', label: 'Mécanisme sévère', type: 'checkbox', points: 1 },
      { id: 'vomiting', label: 'Vomissements (≥ 2 ans: > 1 fois)', type: 'checkbox', points: 1 },
      { id: 'headache', label: 'Céphalées sévères (≥ 2 ans)', type: 'checkbox', points: 1 },
      { id: 'behavior', label: 'Comportement anormal selon parents (< 2 ans)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      // Les critères varient selon l'âge, simplification ici
      let major = 0;
      let minor = 0;
      if (values.gcs) major++;
      if (values.mentalStatus) major++;
      if (values.skull) major++;
      if (values.hematoma) minor++;
      if (values.locFew) minor++;
      if (values.mechanism) minor++;
      if (values.vomiting) minor++;
      if (values.headache) minor++;
      if (values.behavior) minor++;
      return { score: major + minor, max: 9, major: major, minor: minor };
    },
    interpret: (score, calculated) => {
      const major = calculated?.major || 0;
      const minor = calculated?.minor || 0;
      if (major > 0) return { level: 'danger', text: `PECARN: ${major} critère(s) majeur(s)`, detail: 'TDM cérébral recommandé' };
      if (minor >= 2) return { level: 'warning', text: `PECARN: ${minor} critères mineurs`, detail: 'TDM ou observation 4-6h selon préférence' };
      if (minor === 1) return { level: 'warning', text: `PECARN: 1 critère mineur`, detail: 'Observation préférée au TDM' };
      return { level: 'success', text: 'PECARN: Aucun critère', detail: 'TDM non recommandé (risque lésion < 0.02%)' };
    },
    formula: 'PECARN: critères majeurs (TDM) et mineurs (observation/TDM)'
  },

  // SIRS pédiatrique
  sirsPediatric: {
    name: 'SRIS pédiatrique',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Syndrome de réponse inflammatoire systémique',
    color: 'violet',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 'newborn', label: '0-1 semaine' },
        { value: 'neonate', label: '1 sem - 1 mois' },
        { value: 'infant', label: '1 mois - 1 an' },
        { value: 'toddler', label: '1-5 ans' },
        { value: 'child', label: '6-12 ans' },
        { value: 'adolescent', label: '13-18 ans' }
      ]},
      { id: 'temperature', label: 'Température', type: 'select', options: [
        { value: 0, label: 'Normale' },
        { value: 1, label: '> 38.5°C ou < 36°C' }
      ]},
      { id: 'tachycardia', label: 'Tachycardie (selon âge)', type: 'checkbox', points: 1 },
      { id: 'tachypnea', label: 'Tachypnée (selon âge)', type: 'checkbox', points: 1 },
      { id: 'leukocytes', label: 'GB anormaux (leucocytose ou leukopénie selon âge)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = values.temperature || 0;
      if (values.tachycardia) total += 1;
      if (values.tachypnea) total += 1;
      if (values.leukocytes) total += 1;
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `SRIS pédiatrique ${score}/4`, detail: 'Critères non remplis' };
      return { level: 'danger', text: `SRIS pédiatrique ${score}/4`, detail: 'SRIS présent (≥2 critères dont temp ou GB)' };
    },
    formula: 'SRIS pédiatrique: ≥2 critères dont obligatoirement température ou GB'
  },

  // SCORAD - Dermatite atopique
  scorad: {
    name: 'Score SCORAD',
    category: 'Dermatologie',
    icon: User,
    description: 'Sévérité dermatite atopique',
    color: 'pink',
    fields: [
      { id: 'extent', label: 'Surface corporelle atteinte (%)', type: 'number', min: 0, max: 100 },
      { id: 'erythema', label: 'Érythème', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Léger' },
        { value: 2, label: '2 - Modéré' }, { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'edema', label: 'Œdème/Papules', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'oozing', label: 'Suintement/Croûtes', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'excoriation', label: 'Excoriations', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'lichenification', label: 'Lichénification', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'dryness', label: 'Sécheresse (zones non atteintes)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'pruritus', label: 'Prurit (0-10)', type: 'number', min: 0, max: 10 },
      { id: 'sleepLoss', label: 'Perte sommeil (0-10)', type: 'number', min: 0, max: 10 }
    ],
    calculate: (values) => {
      const A = (values.extent || 0) / 5; // 0-20
      const B = 3.5 * ((values.erythema || 0) + (values.edema || 0) + (values.oozing || 0) + 
                       (values.excoriation || 0) + (values.lichenification || 0) + (values.dryness || 0)); // 0-63
      const C = (values.pruritus || 0) + (values.sleepLoss || 0); // 0-20
      const total = A + B + C;
      return { score: total.toFixed(1), max: 103 };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 25) return { level: 'success', text: `SCORAD ${score}`, detail: 'Dermatite atopique légère' };
      if (s < 50) return { level: 'warning', text: `SCORAD ${score}`, detail: 'Dermatite atopique modérée' };
      return { level: 'danger', text: `SCORAD ${score}`, detail: 'Dermatite atopique sévère' };
    },
    formula: 'SCORAD = A/5 + 3.5B + C (A=surface, B=intensité, C=subjectif)'
  },

  // STESS - Crises convulsives
  stess: {
    name: 'Score STESS',
    category: 'Neurologie',
    icon: Brain,
    description: 'Pronostic état de mal épileptique',
    color: 'purple',
    fields: [
      { id: 'consciousness', label: 'Conscience avant traitement', type: 'select', options: [
        { value: 0, label: 'Alerte ou somnolent (0 pt)' },
        { value: 1, label: 'Stuporeux ou comateux (1 pt)' }
      ]},
      { id: 'seizureType', label: 'Type de crise le plus grave', type: 'select', options: [
        { value: 0, label: 'Partielle simple (0 pt)' },
        { value: 1, label: 'Partielle complexe (1 pt)' },
        { value: 2, label: 'Généralisée convulsive (2 pts)' }
      ]},
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 65 ans (0 pt)' },
        { value: 2, label: '≥ 65 ans (2 pts)' }
      ]},
      { id: 'history', label: 'ATCD épilepsie', type: 'select', options: [
        { value: 0, label: 'Oui (0 pt)' },
        { value: 1, label: 'Non ou inconnu (1 pt)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `STESS ${score}`, detail: 'Pronostic favorable - Mortalité ~3%' };
      if (score <= 4) return { level: 'warning', text: `STESS ${score}`, detail: 'Pronostic intermédiaire' };
      return { level: 'danger', text: `STESS ${score}`, detail: 'Pronostic défavorable - Mortalité ~40%' };
    },
    formula: 'STESS = Conscience + Type crise + Âge + ATCD épilepsie'
  },

  // St Andre TVP
  stAndre: {
    name: 'Score de St André (TVP)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Probabilité phlébite proximale ou distale',
    color: 'rose',
    fields: [
      { id: 'male', label: 'Sexe masculin', type: 'checkbox', points: 1 },
      { id: 'paralysis', label: 'Paralysie/Parrésie/Immobilisation plâtrée MI', type: 'checkbox', points: 1 },
      { id: 'bedridden', label: 'Alitement > 3 jours ou chirurgie < 4 sem', type: 'checkbox', points: 1 },
      { id: 'tenderness', label: 'Douleur trajet veineux', type: 'checkbox', points: 1 },
      { id: 'swelling', label: 'Gonflement mollet > 3cm vs autre jambe', type: 'checkbox', points: 1 },
      { id: 'pitting', label: 'Œdème prenant le godet', type: 'checkbox', points: 1 },
      { id: 'collateral', label: 'Veines collatérales superficielles', type: 'checkbox', points: 1 },
      { id: 'alternative', label: 'Diagnostic alternatif aussi probable', type: 'checkbox', points: -2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.male) total += 1;
      if (values.paralysis) total += 1;
      if (values.bedridden) total += 1;
      if (values.tenderness) total += 1;
      if (values.swelling) total += 1;
      if (values.pitting) total += 1;
      if (values.collateral) total += 1;
      if (values.alternative) total -= 2;
      return { score: total, max: 7, min: -2 };
    },
    interpret: (score) => {
      if (score <= 0) return { level: 'success', text: `St André ${score}`, detail: 'TVP peu probable - D-dimères' };
      if (score <= 2) return { level: 'warning', text: `St André ${score}`, detail: 'TVP distale possible - D-dimères puis écho si +' };
      return { level: 'danger', text: `St André ${score}`, detail: 'TVP proximale probable - Écho-doppler' };
    },
    formula: 'St André: variante du Wells TVP pour différencier proximal/distal'
  },

  // Toronto - Besoins énergétiques brûlé
  toronto: {
    name: 'Formule de Toronto',
    category: 'Brûlés',
    icon: Flame,
    description: 'Besoins énergétiques patient brûlé',
    color: 'orange',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 200 },
      { id: 'scb', label: 'Surface corporelle brûlée (%)', type: 'number', min: 1, max: 100 },
      { id: 'dayPostBurn', label: 'Jour post-brûlure', type: 'number', min: 1, max: 100 },
      { id: 'fever', label: 'Fièvre > 38.5°C', type: 'checkbox', points: 0 },
      { id: 'ventilation', label: 'Ventilation mécanique', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      const weight = values.weight || 70;
      const scb = values.scb || 20;
      const day = values.dayPostBurn || 1;
      
      // Toronto formula: -4343 + (10.5×%SCB) + (0.23×Cal intake) + (0.84×Harris-Benedict) + (114×Temp) - (4.5×Day)
      // Simplification: approximation basée sur poids et SCB
      const base = 25 * weight; // Approximation Harris-Benedict
      const stressFactor = 1 + (scb / 100); // Facteur stress lié à SCB
      let calories = base * stressFactor;
      
      // Ajustements
      if (values.fever) calories *= 1.1;
      if (values.ventilation) calories *= 0.9; // Réduction si ventilé sédaté
      
      return { score: Math.round(calories), max: '', unit: 'kcal/j' };
    },
    interpret: (score) => {
      return { level: 'warning', text: `Besoins: ~${score} kcal/j`, detail: 'Estimation - Ajuster selon calorimétrie indirecte si disponible' };
    },
    formula: 'Toronto: estimation besoins énergétiques brûlés'
  },

  // Uropath - Score urologique
  ipss: {
    name: 'Score IPSS',
    category: 'Urologie',
    icon: User,
    description: 'Symptômes urinaires liés à l\'HBP',
    color: 'blue',
    fields: [
      { id: 'incomplete', label: 'Sensation de vidange incomplète', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - <1/5' }, { value: 2, label: '2 - <1/2' },
        { value: 3, label: '3 - ~1/2' }, { value: 4, label: '4 - >1/2' }, { value: 5, label: '5 - Presque toujours' }
      ]},
      { id: 'frequency', label: 'Fréquence mictionnelle (<2h)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'intermittency', label: 'Intermittence du jet', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'urgency', label: 'Impériosité', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'weakStream', label: 'Jet faible', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'straining', label: 'Poussée abdominale', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }
      ]},
      { id: 'nocturia', label: 'Nycturie (levés/nuit)', type: 'select', options: [
        { value: 0, label: '0 fois' }, { value: 1, label: '1 fois' }, { value: 2, label: '2 fois' },
        { value: 3, label: '3 fois' }, { value: 4, label: '4 fois' }, { value: 5, label: '5 fois ou +' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 35 };
    },
    interpret: (score) => {
      if (score <= 7) return { level: 'success', text: `IPSS ${score}/35`, detail: 'Symptômes légers - Surveillance' };
      if (score <= 19) return { level: 'warning', text: `IPSS ${score}/35`, detail: 'Symptômes modérés - Traitement médical à discuter' };
      return { level: 'danger', text: `IPSS ${score}/35`, detail: 'Symptômes sévères - Traitement indiqué' };
    },
    formula: 'IPSS = 7 questions (0-5 chacune). <8 léger, 8-19 modéré, >19 sévère'
  },

  // WIfI - Plaies ischémiques
  wifi: {
    name: 'Classification WIfI',
    category: 'Chirurgie vasculaire',
    icon: Heart,
    description: 'Plaies chroniques ischémiques',
    color: 'red',
    fields: [
      { id: 'wound', label: 'Plaie (W)', type: 'select', options: [
        { value: 0, label: '0 - Pas de plaie' },
        { value: 1, label: '1 - Ulcère petit, superficiel' },
        { value: 2, label: '2 - Ulcère profond, gangrène limitée' },
        { value: 3, label: '3 - Ulcère extensif, gangrène majeure' }
      ]},
      { id: 'ischemia', label: 'Ischémie (I)', type: 'select', options: [
        { value: 0, label: '0 - IPS > 0.8, PSO > 100mmHg' },
        { value: 1, label: '1 - IPS 0.6-0.8, PSO 70-100' },
        { value: 2, label: '2 - IPS 0.4-0.6, PSO 50-70' },
        { value: 3, label: '3 - IPS < 0.4, PSO < 50' }
      ]},
      { id: 'infection', label: 'Infection (fI)', type: 'select', options: [
        { value: 0, label: '0 - Pas d\'infection' },
        { value: 1, label: '1 - Infection locale (dermohypodermite < 2cm)' },
        { value: 2, label: '2 - Infection locale avec signes généraux' },
        { value: 3, label: '3 - Sepsis, SRIS' }
      ]}
    ],
    calculate: (values) => {
      const w = values.wound || 0;
      const i = values.ischemia || 0;
      const f = values.infection || 0;
      return { score: `W${w}I${i}fI${f}`, max: 'W3I3fI3', wound: w, ischemia: i, infection: f };
    },
    interpret: (score, calculated) => {
      const total = (calculated?.wound || 0) + (calculated?.ischemia || 0) + (calculated?.infection || 0);
      if (total <= 2) return { level: 'success', text: score, detail: 'Faible risque d\'amputation' };
      if (total <= 5) return { level: 'warning', text: score, detail: 'Risque intermédiaire - Revascularisation à considérer' };
      return { level: 'danger', text: score, detail: 'Haut risque d\'amputation - Revascularisation urgente' };
    },
    formula: 'WIfI: chaque composant 0-3. Risque amputation selon combinaison'
  },

  // Zona - Immunodépression
  zonaImmuno: {
    name: 'Critères Zona compliqué',
    category: 'Infectiologie',
    icon: Thermometer,
    description: 'Indication traitement antiviral zona',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge > 50 ans', type: 'checkbox', points: 1 },
      { id: 'ophthalmic', label: 'Zona ophtalmique', type: 'checkbox', points: 1 },
      { id: 'immunosuppression', label: 'Immunodépression', type: 'checkbox', points: 1 },
      { id: 'severe', label: 'Forme sévère/nécrotique/hémorragique', type: 'checkbox', points: 1 },
      { id: 'disseminated', label: 'Zona disséminé (> 2 dermatomes)', type: 'checkbox', points: 1 },
      { id: 'complications', label: 'Complications neurologiques', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 6 };
    },
    interpret: (score, calculated, values) => {
      const urgent = values.ophthalmic || values.immunosuppression || values.disseminated || values.complications;
      if (urgent) return { level: 'danger', text: `Zona compliqué (${score} critères)`, detail: 'Antiviral IV urgent, avis spécialisé' };
      if (score >= 1) return { level: 'warning', text: `Zona à risque (${score} critère${score > 1 ? 's' : ''})`, detail: 'Antiviral oral (valaciclovir) dans les 72h' };
      return { level: 'success', text: 'Zona simple', detail: 'Traitement symptomatique, antiviral non systématique' };
    },
    formula: 'Zona compliqué: traitement antiviral si ≥1 critère'
  }
};
